import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Grow, Menu, MenuItem, TextField } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import PauseCircleRoundedIcon from "@mui/icons-material/PauseCircleRounded";
import DonutLargeRoundedIcon from "@mui/icons-material/DonutLargeRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import PestControlRoundedIcon from "@mui/icons-material/PestControlRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import KeyboardDoubleArrowUpRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowUpRounded";
import KeyboardDoubleArrowDownRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowDownRounded";
import DragHandleRoundedIcon from "@mui/icons-material/DragHandleRounded";
import CKTextEditor from "../CK-Text-Editor/index";
import { firstLetterOf, userAvatarColor } from "../../utilities/userAvatar";

/* --------------------------------------------------------------------------
   Building blocks for the issue-detail popup (TaskWorkFlow.jsx).

   The shared SelectMenuItems / IssueTypeMenuList / SearchableDropdown /
   LabalsDropDown components are used on other screens with their own looks,
   so the popup carries purpose-built controls instead of restyling those.
   Every control is fully controlled by the value it is given — the popup
   owns the issue and nothing here keeps a second copy of it.
   -------------------------------------------------------------------------- */

/* ------------------------------------------------------------------ glyphs */

const meta = (Icon, tone, motion) => ({ Icon, tone, motion });

/* `motion` names a keyframe in taskWorkFlow.scss. "In Progress" is the one
   status that keeps moving, because it describes something still happening;
   the rest play once when they appear and again on hover. */
const STATUS_META = {
  "To Do": meta(RadioButtonUncheckedRoundedIcon, "slate", "pulse"),
  "In Progress": meta(AutorenewRoundedIcon, "blue", "spin"),
  "Code Review": meta(RateReviewRoundedIcon, "amber", "bob"),
  "In Review": meta(RateReviewRoundedIcon, "amber", "bob"),
  "In Testing": meta(ScienceRoundedIcon, "violet", "wobble"),
  QA: meta(ScienceRoundedIcon, "violet", "wobble"),
  "Feedback/Hold": meta(PauseCircleRoundedIcon, "red", "pulse"),
  "Production Ready": meta(RocketLaunchRoundedIcon, "teal", "lift"),
  Done: meta(CheckCircleRoundedIcon, "green", "pop"),
};
const FALLBACK_STATUS = meta(DonutLargeRoundedIcon, "slate", "pulse");

const TYPE_META = {
  Story: meta(BookmarkRoundedIcon, "amber", "bob"),
  Bug: meta(PestControlRoundedIcon, "red", "wiggle"),
  Task: meta(TaskAltRoundedIcon, "blue", "pop"),
  Epic: meta(BoltRoundedIcon, "violet", "flash"),
};

const PRIORITY_META = {
  Urgent: meta(NotificationsActiveRoundedIcon, "amber", "ring"),
  High: meta(KeyboardDoubleArrowUpRoundedIcon, "red", "nudge-up"),
  Medium: meta(DragHandleRoundedIcon, "blue", "breathe"),
  Low: meta(KeyboardDoubleArrowDownRoundedIcon, "teal", "nudge-down"),
};

export const getStatusMeta = (status) => STATUS_META[status] || FALLBACK_STATUS;
export const getTypeMeta = (type) => TYPE_META[type] || TYPE_META.Story;
export const getPriorityMeta = (priority) => PRIORITY_META[priority] || PRIORITY_META.Medium;

export const Glyph = ({ meta: { Icon, tone, motion }, className = "" }) => (
  <span className={`pj-glyph pj-tone--${tone} pj-motion--${motion} ${className}`} aria-hidden="true">
    <Icon />
  </span>
);

/* ------------------------------------------------------------------ people */

const hueOf = (text = "") => {
  let hue = 0;
  for (let i = 0; i < text.length; i += 1) hue = (hue * 31 + text.charCodeAt(i)) % 360;
  return hue;
};

const initialsOf = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${parts[0][0] || ""}${last || ""}`.toUpperCase();
};

/* A person is drawn as their own photo when they have uploaded one (`src`),
   and otherwise as a letter on one flat colour from the shared ten-colour
   palette — no gradients, and the colour comes from the name, so one person
   reads the same on every screen and two people in a list rarely land on the
   same fill. `solid` is what the people dropdowns ask for: a single letter
   rather than two initials. */
export const PersonAvatar = ({ name, size = 28, title, solid = false, src = "" }) => {
  const [photoFailed, setPhotoFailed] = useState(false);
  const style = { "--s": `${size}px` };

  /* a recycled cell may be handed a different person's photo */
  useEffect(() => setPhotoFailed(false), [src]);

  if (!name) {
    return (
      <span className="pj-avatar is-empty" style={style} title={title || "Unassigned"}>
        <PersonOutlineRoundedIcon />
      </span>
    );
  }

  if (src && !photoFailed) {
    return (
      <span className="pj-avatar is-photo" title={title || name} style={style}>
        <img src={src} alt="" onError={() => setPhotoFailed(true)} />
      </span>
    );
  }

  return (
    <span className="pj-avatar" title={title || name} style={{ ...style, background: userAvatarColor(name) }}>
      {solid ? firstLetterOf(name) : initialsOf(name)}
    </span>
  );
};

/* Contributors are { employeeId, employeeName, employeeEmail, role }; that is
   the identity every people control compares on. */
export const personKey = (person) =>
  String(person?.employeeId || person?.employeeEmail || person?.employeeName || "");

export const personName = (person) => person?.employeeName || person?.employeeEmail || "";

const UNASSIGNED = { employeeName: "Unassigned", __unassigned: true };

/* Autocomplete re-syncs its text whenever getOptionLabel / onInputChange change
   identity, which would wipe what the user is typing — so these stay stable. */
const personOptionLabel = (option) => (option?.__unassigned ? "Unassigned" : personName(option));
const labelOptionLabel = (option) => (typeof option === "string" ? option : `Create "${option.create}"`);

const popperSlots = {
  paper: { className: "pj-issue-pop" },
  popper: { className: "pj-issue-popper" },
};

export const PeoplePicker = ({ value, members = [], onSelect, ariaLabel, placeholder = "Search people…" }) => {
  const options = useMemo(() => {
    const list = [UNASSIGNED, ...members.filter(Boolean)];
    if (value && !list.some((option) => personKey(option) === personKey(value))) list.push(value);
    return list;
  }, [members, value]);

  const current = value || UNASSIGNED;

  return (
    <Autocomplete
      className="pj-field"
      value={current}
      options={options}
      disableClearable
      openOnFocus
      autoHighlight
      popupIcon={<ExpandMoreRoundedIcon />}
      slotProps={popperSlots}
      getOptionLabel={personOptionLabel}
      isOptionEqualToValue={(option, selected) =>
        option?.__unassigned ? Boolean(selected?.__unassigned) : personKey(option) === personKey(selected)
      }
      onChange={(event, next) => {
        const picked = next?.__unassigned ? null : next;
        const unchanged = picked ? personKey(picked) === personKey(value) : !value;
        if (!unchanged) onSelect(picked);
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest} className={`${rest.className || ""} pj-opt`}>
            <PersonAvatar name={option.__unassigned ? "" : personName(option)} size={30} solid />
            <span className="pj-opt__text">
              <span className="pj-opt__title">{option.__unassigned ? "Unassigned" : personName(option)}</span>
              {!option.__unassigned && option.employeeEmail && option.employeeName ? (
                <span className="pj-opt__sub">{option.employeeEmail}</span>
              ) : null}
            </span>
            {selected ? <CheckRoundedIcon className="pj-opt__check" /> : null}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder}
          inputProps={{ ...params.inputProps, "aria-label": ariaLabel }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <span className="pj-field__lead">
                <PersonAvatar name={value ? personName(value) : ""} size={24} solid />
              </span>
            ),
          }}
        />
      )}
    />
  );
};

/* ------------------------------------------------------------------ labels */

export const labelStyle = (name = "") => {
  const hue = hueOf(name);
  return {
    background: `linear-gradient(135deg, hsl(${hue} 72% 94%), hsl(${(hue + 30) % 360} 72% 90%))`,
    color: `hsl(${hue} 55% 30%)`,
    borderColor: `hsl(${hue} 60% 84%)`,
  };
};

export const LabelPicker = ({ value, labels = [], onSelect, onCreate }) => {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  // the chosen label shows as a chip, so the text box stays free for searching
  const handleInputChange = useMemo(
    () => (event, next, reason) => setInput(reason === "reset" ? "" : next),
    []
  );

  const names = useMemo(() => {
    const list = [];
    labels.forEach((label) => {
      const name = typeof label === "string" ? label : label?.name || label?.value;
      if (name && !list.includes(name)) list.push(name);
    });
    if (value && !list.includes(value)) list.unshift(value);
    return list;
  }, [labels, value]);

  return (
    <Autocomplete
      className="pj-field pj-field--label"
      value={value || null}
      options={names}
      inputValue={input}
      openOnFocus
      autoHighlight
      disabled={busy}
      popupIcon={<ExpandMoreRoundedIcon />}
      slotProps={popperSlots}
      noOptionsText="Type to create a label"
      getOptionLabel={labelOptionLabel}
      isOptionEqualToValue={(option, selected) => option === selected}
      filterOptions={(options, { inputValue }) => {
        const query = inputValue.trim().toLowerCase();
        const matches = options.filter((option) => typeof option === "string" && option.toLowerCase().includes(query));
        const exists = options.some((option) => typeof option === "string" && option.toLowerCase() === query);
        return query && !exists ? [...matches, { create: inputValue.trim() }] : matches;
      }}
      onInputChange={handleInputChange}
      onChange={async (event, next) => {
        setInput("");
        if (next && typeof next === "object" && next.create) {
          setBusy(true);
          const created = await onCreate(next.create);
          setBusy(false);
          if (created) onSelect(next.create);
          return;
        }
        const name = next || "";
        if (name !== (value || "")) onSelect(name);
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...rest } = props;
        if (typeof option !== "string") {
          return (
            <li key={key} {...rest} className={`${rest.className || ""} pj-opt pj-opt--create`}>
              <span className="pj-opt__plus"><AddRoundedIcon /></span>
              <span className="pj-opt__title">Create “{option.create}”</span>
            </li>
          );
        }
        return (
          <li key={key} {...rest} className={`${rest.className || ""} pj-opt`}>
            <span className="pj-label-chip" style={labelStyle(option)}>{option}</span>
            {selected ? <CheckRoundedIcon className="pj-opt__check" /> : null}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={value ? "" : "Add a label"}
          inputProps={{ ...params.inputProps, "aria-label": "Label" }}
          InputProps={{
            ...params.InputProps,
            startAdornment: value ? (
              <span className="pj-label-chip pj-label-chip--in" style={labelStyle(value)} key={value}>
                {value}
              </span>
            ) : null,
          }}
        />
      )}
    />
  );
};

/* ------------------------------------------------------------ pill select */

export const PillMenu = ({ value, options = [], getMeta, onSelect, variant = "pill", ariaLabel, placeholder = "Select" }) => {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const current = getMeta(value);

  return (
    <>
      <button
        type="button"
        className={`pj-select pj-select--${variant} pj-tone--${current.tone} pj-anim-host ${open ? "is-open" : ""}`}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <Glyph meta={current} key={value} />
        <span className="pj-select__label">{value || placeholder}</span>
        <ExpandMoreRoundedIcon className="pj-select__chevron" />
      </button>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        TransitionComponent={Grow}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { className: "pj-issue-menu", style: { minWidth: Math.max(anchor?.offsetWidth || 0, 200) } },
        }}
      >
        {options.map((option, index) => {
          const optionMeta = getMeta(option);
          const selected = option === value;
          return (
            <MenuItem
              key={option}
              selected={selected}
              className="pj-issue-menu__item pj-anim-host"
              style={{ "--i": index }}
              onClick={() => {
                setAnchor(null);
                if (!selected) onSelect(option);
              }}
            >
              <Glyph meta={optionMeta} />
              <span className="pj-issue-menu__text">{option}</span>
              {selected ? <CheckRoundedIcon className="pj-issue-menu__check" /> : null}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

/* ---------------------------------------------------------------- buttons */

export const PjButton = ({ variant = "primary", size, busy = false, icon = null, className = "", children, ...rest }) => (
  <button
    type="button"
    className={`pj-ibtn pj-ibtn--${variant} ${size ? `pj-ibtn--${size}` : ""} ${busy ? "is-busy" : ""} ${className}`}
    {...rest}
    disabled={busy || rest.disabled}
  >
    {busy ? <span className="pj-spinner" aria-hidden="true" /> : icon ? <span className="pj-ibtn__icon">{icon}</span> : null}
    <span>{children}</span>
  </button>
);

export const SaveIndicator = ({ state }) => (
  <span className={`pj-save is-${state}`} role="status" aria-live="polite">
    {state === "saving" ? (
      <>
        <span className="pj-spinner pj-spinner--light" aria-hidden="true" />
        Saving…
      </>
    ) : null}
    {state === "saved" ? (
      <>
        <CloudDoneRoundedIcon className="pj-save__icon" />
        All changes saved
      </>
    ) : null}
    {state === "error" ? (
      <>
        <ErrorOutlineRoundedIcon className="pj-save__icon" />
        Not saved
      </>
    ) : null}
  </span>
);

/* -------------------------------------------------------------- rich text */

const plainText = (html = "") =>
  String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .trim();

/* CKEditor hands back "<p>&nbsp;</p>" for an emptied editor, so emptiness is
   judged on the text, with images counting as content. */
export const hasRichContent = (html) => Boolean(html) && (plainText(html).length > 0 || /<img/i.test(html));

export const RichTextBlock = ({ html, placeholder, onEdit, onImage, icon }) => {
  if (!hasRichContent(html)) {
    return (
      <button type="button" className="pj-empty-cta pj-anim-host" onClick={onEdit}>
        <span className="pj-empty-cta__icon pj-motion--bob">{icon}</span>
        {placeholder}
      </button>
    );
  }
  return (
    <div
      className="pj-rich pj-rich--editable"
      role="button"
      tabIndex={0}
      title="Click to edit"
      onKeyDown={(event) => {
        if (event.key === "Enter" && event.target === event.currentTarget) onEdit();
      }}
      onClick={(event) => {
        const image = event.target.closest?.("img");
        if (image) {
          onImage?.(image.src);
          return;
        }
        if (event.target.closest?.("a")) return;
        onEdit();
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export const EditorBlock = ({ value, onChange, onSave, onCancel, saving = false, saveLabel = "Save", error = "", autoFocus = false }) => (
  <div
    className="pj-editor-wrap"
    onKeyDown={(event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        onSave();
      }
    }}
  >
    <CKTextEditor payloadText={value} onChange={onChange} autoFocus={autoFocus} />
    {error ? <span className="pj-field-error">{error}</span> : null}
    <div className="pj-editor-actions">
      <span className="pj-hint">
        <kbd>Ctrl</kbd> + <kbd>Enter</kbd> to save
      </span>
      <PjButton variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
        Cancel
      </PjButton>
      <PjButton size="sm" busy={saving} onClick={onSave} icon={<CheckRoundedIcon />}>
        {saving ? "Saving…" : saveLabel}
      </PjButton>
    </div>
  </div>
);

export const DetailRow = ({ icon, label, motion = "bob", children }) => (
  <div className="pj-row pj-anim-host">
    <span className="pj-row__label">
      <span className={`pj-row__icon pj-motion--${motion}`}>{icon}</span>
      {label}
    </span>
    <div className="pj-row__value">{children}</div>
  </div>
);
